import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Search, X, Check, MapPin, Loader2 } from 'lucide-react';

/**
 * Searchable multi-select component for preferred locations
 * Features:
 * - Search with debounce (200ms)
 * - Top locations when search is empty
 * - Keyboard support (Enter to select, Backspace to remove)
 * - Max 10 selections
 * - No duplicates
 */
const LocationMultiSelect = ({ 
  locations = [],
  selectedLocations = [],
  onChange,
  cityName = '',
  isLoading = false,
  maxSelections = 10,
  topLocationsCount = 8,
  placeholder = "Search locations..."
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Debounce search query (200ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 200);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset highlight when filtered results change
  useEffect(() => {
    setHighlightedIndex(0);
  }, [debouncedQuery]);

  // Filter locations based on search query (case-insensitive substring match)
  const filteredLocations = useMemo(() => {
    const availableLocations = locations.filter(loc => !selectedLocations.includes(loc));
    
    if (!debouncedQuery.trim()) {
      // Show top locations when search is empty
      return availableLocations.slice(0, topLocationsCount);
    }
    
    const query = debouncedQuery.toLowerCase().trim();
    return availableLocations.filter(loc => 
      loc.toLowerCase().includes(query)
    );
  }, [locations, selectedLocations, debouncedQuery, topLocationsCount]);

  // Check if max selections reached
  const isMaxReached = selectedLocations.length >= maxSelections;

  // Add location to selection
  const addLocation = useCallback((location) => {
    if (selectedLocations.includes(location) || isMaxReached) return;
    
    const newSelection = [...selectedLocations, location];
    onChange(newSelection);
    setSearchQuery('');
    setHighlightedIndex(0);
    inputRef.current?.focus();
  }, [selectedLocations, onChange, isMaxReached]);

  // Remove location from selection
  const removeLocation = useCallback((location, e) => {
    if (e) e.stopPropagation();
    const newSelection = selectedLocations.filter(loc => loc !== location);
    onChange(newSelection);
  }, [selectedLocations, onChange]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredLocations.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredLocations.length > 0 && highlightedIndex < filteredLocations.length) {
          addLocation(filteredLocations[highlightedIndex]);
        }
        break;
      case 'Backspace':
        if (searchQuery === '' && selectedLocations.length > 0) {
          // Remove last selected location
          removeLocation(selectedLocations[selectedLocations.length - 1]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSearchQuery('');
        break;
      default:
        break;
    }
  };

  // Scroll highlighted item into view
  useEffect(() => {
    if (listRef.current && highlightedIndex >= 0) {
      const highlightedElement = listRef.current.children[highlightedIndex];
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex]);

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  return (
    <div ref={containerRef} className="relative">
      {/* Search Input and Selected Chips Container */}
      <div 
        className={`min-h-[44px] w-full px-3 py-2 rounded-xl border bg-white transition-all
          ${isOpen ? 'border-[#5B3DF5] ring-2 ring-[#5B3DF5]/20' : 'border-slate-200 hover:border-slate-300'}
        `}
        onClick={() => inputRef.current?.focus()}
      >
        {/* Selected Locations as Chips */}
        {selectedLocations.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2">
            {selectedLocations.map((location) => (
              <span
                key={location}
                className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#5B3DF5] text-white text-xs rounded-full font-medium"
              >
                <span className="truncate max-w-[120px]">{location}</span>
                <button
                  type="button"
                  onClick={(e) => removeLocation(location, e)}
                  className="hover:bg-white/20 rounded-full p-0.5 transition-colors"
                  data-testid={`remove-location-${location}`}
                >
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        )}

        {/* Search Input */}
        <div className="relative flex items-center">
          <Search size={16} className="text-slate-400 mr-2 flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            data-testid="location-search-input"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={handleInputFocus}
            onKeyDown={handleKeyDown}
            placeholder={isMaxReached ? `Max ${maxSelections} locations reached` : placeholder}
            disabled={isMaxReached}
            className="flex-1 text-sm outline-none bg-transparent placeholder:text-slate-400 disabled:cursor-not-allowed"
          />
          {isLoading && (
            <Loader2 size={16} className="text-slate-400 animate-spin flex-shrink-0" />
          )}
        </div>
      </div>

      {/* Dropdown Panel */}
      {isOpen && !isLoading && locations.length > 0 && (
        <div 
          className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden"
          style={{ maxHeight: '280px' }}
        >
          {/* Header - shows context */}
          <div className="px-3 py-2 bg-slate-50 border-b border-slate-100">
            {!debouncedQuery.trim() ? (
              <p className="text-xs text-slate-500 font-medium flex items-center gap-1">
                <MapPin size={12} />
                Top locations in {cityName || 'your city'}
              </p>
            ) : (
              <p className="text-xs text-slate-500">
                {filteredLocations.length} location{filteredLocations.length !== 1 ? 's' : ''} found
              </p>
            )}
          </div>

          {/* Location List */}
          <div 
            ref={listRef}
            className="overflow-y-auto"
            style={{ maxHeight: '220px' }}
          >
            {filteredLocations.length === 0 ? (
              <div className="px-4 py-6 text-center">
                <MapPin size={24} className="mx-auto text-slate-300 mb-2" />
                <p className="text-sm text-slate-500">No locations match "{searchQuery}"</p>
                <p className="text-xs text-slate-400 mt-1">Try a different search term</p>
              </div>
            ) : (
              filteredLocations.map((location, index) => {
                const isHighlighted = index === highlightedIndex;
                return (
                  <div
                    key={location}
                    data-testid={`location-option-${location}`}
                    onClick={() => addLocation(location)}
                    className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors
                      ${isHighlighted ? 'bg-[#5B3DF5]/10' : 'hover:bg-slate-50'}
                      ${index !== filteredLocations.length - 1 ? 'border-b border-slate-50' : ''}
                    `}
                  >
                    <MapPin size={14} className={`flex-shrink-0 ${isHighlighted ? 'text-[#5B3DF5]' : 'text-slate-400'}`} />
                    <span className={`text-sm ${isHighlighted ? 'font-medium text-[#5B3DF5]' : 'text-slate-700'}`}>
                      {location}
                    </span>
                    {isHighlighted && (
                      <span className="ml-auto text-[10px] text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">
                        Enter ↵
                      </span>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Footer with selection info */}
          <div className="px-3 py-2 border-t border-slate-100 bg-slate-50 flex items-center justify-between">
            <span className="text-xs text-slate-500">
              {selectedLocations.length}/{maxSelections} selected
            </span>
            {selectedLocations.length > 0 && (
              <button
                type="button"
                onClick={() => onChange([])}
                className="text-xs text-[#5B3DF5] hover:text-[#4A2FE0] font-medium"
              >
                Clear all
              </button>
            )}
          </div>
        </div>
      )}

      {/* Info text */}
      {!isOpen && selectedLocations.length > 0 && (
        <p className="text-xs text-slate-400 mt-1.5 ml-1">
          {selectedLocations.length} location{selectedLocations.length !== 1 ? 's' : ''} selected
        </p>
      )}
    </div>
  );
};

export default LocationMultiSelect;
